import React, { useCallback, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { ALL_OPTION_KEY } from '@/common/constants';
import { OptionKey } from '@/common/types';
import { IAuthorizedUserDtoStatusEnum } from '@/dtos';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import useCreateAuthorizedUserQuery from './queries/useCreateAuthorizedUser.query';
import useGetAuthorizedUsersListQuery from './queries/useGetAuthorizedUsersList.query';
import useUpdateAuthorizedUserQuery from './queries/useUpdateAuthorizedUser.query';
import useUpdateAuthorizedUserStatusQuery, {
  AuthorizedUserStatusAction,
} from './queries/useUpdateAuthorizedUserStatus.query';
import {
  AuthorizedUser,
  CreateAuthorizedUserParams,
  UpdateAuthorizedUserParams,
} from './authorizedUsers.types';
import AuthorizedUsersComponent from './components';

const ERROR_SNACKBAR_TEXT = 'Something went wrong. Please try again later.';

const getFullName = (user?: AuthorizedUser | null) =>
  user ? `${user.firstName} ${user.lastName}` : '';

const AuthorizedUsersContainer: React.FC = () => {
  const { params: { id: employeeId } } = useRouteMatch<{ id: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AuthorizedUser | null>(null);
  const [deactivatingUser, setDeactivatingUser] = useState<AuthorizedUser | null>(null);
  const [reactivatingUser, setReactivatingUser] = useState<AuthorizedUser | null>(null);
  const [filterStatuses, setFilterStatuses] = useState<OptionKey[]>([IAuthorizedUserDtoStatusEnum.ACTIVE]);

  const { authorizedUsers, isLoading } = useGetAuthorizedUsersListQuery(employeeId);
  const { handleAddPermanentSnackbar } = useSnackbar();

  const showSnackbar = useCallback(
    (text: string, state: SnackbarState) => {
      handleAddPermanentSnackbar({ text, state, closeIcon: true, autoRemove: true });
    },
    [handleAddPermanentSnackbar]
  );

  const runWithSnackbar = useCallback(
    async (
      mutate: () => Promise<unknown>,
      successText: string,
      successState: SnackbarState = SnackbarState.positive
    ) => {
      try {
        await mutate();
        showSnackbar(successText, successState);
      } catch {
        showSnackbar(ERROR_SNACKBAR_TEXT, SnackbarState.negative);
      }
    },
    [showSnackbar]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
  }, []);

  const handleCloseDeactivateModal = useCallback(() => {
    setDeactivatingUser(null);
  }, []);

  const handleCloseReactivateModal = useCallback(() => {
    setReactivatingUser(null);
  }, []);

  const handleCloseStatusModals = useCallback(() => {
    setDeactivatingUser(null);
    setReactivatingUser(null);
  }, []);

  const {
    createAuthorizedUser,
    isPending: isCreating,
  } = useCreateAuthorizedUserQuery(employeeId, handleCloseModal);
  const {
    updateAuthorizedUser,
    isPending: isUpdating,
  } = useUpdateAuthorizedUserQuery(editingUser?.id, handleCloseModal);
  const {
    updateAuthorizedUserStatus,
    isPending: isStatusUpdating,
  } = useUpdateAuthorizedUserStatusQuery(handleCloseStatusModals);

  const handleFilterChange = useCallback((statuses: OptionKey[]) => {
    if (!statuses.length) return setFilterStatuses([ALL_OPTION_KEY]);
    const last = statuses[statuses.length - 1];
    if (last === ALL_OPTION_KEY) return setFilterStatuses([ALL_OPTION_KEY]);
    if (statuses[0] === ALL_OPTION_KEY && statuses.length > 1) {
      return setFilterStatuses(statuses.filter((s) => s !== ALL_OPTION_KEY));
    }
    setFilterStatuses(statuses);
  }, []);

  const sortedUsers = useMemo(
    () =>
      [...authorizedUsers].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [authorizedUsers]
  );

  const filteredUsers = useMemo(() => {
    if (filterStatuses.includes(ALL_OPTION_KEY)) {
      const isActive = (user: AuthorizedUser) => user.status === IAuthorizedUserDtoStatusEnum.ACTIVE;
      return [...sortedUsers.filter(isActive), ...sortedUsers.filter((user) => !isActive(user))];
    }
    return sortedUsers.filter((user) => filterStatuses.includes(user.status));
  }, [sortedUsers, filterStatuses]);

  const handleOpenAddModal = useCallback(() => {
    setEditingUser(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((user: AuthorizedUser) => {
    setEditingUser(user);
    setIsModalOpen(true);
  }, []);

  const handleCreate = useCallback(
    (params: CreateAuthorizedUserParams) =>
      runWithSnackbar(
        () => createAuthorizedUser(params),
        `${params.firstName} ${params.lastName} has been added`
      ),
    [createAuthorizedUser, runWithSnackbar]
  );

  const handleUpdate = useCallback(
    async (params: UpdateAuthorizedUserParams) => {
      if (!editingUser) return;
      await runWithSnackbar(
        () => updateAuthorizedUser(params),
        `${params.firstName} ${params.lastName} has been edited`
      );
    },
    [updateAuthorizedUser, editingUser, runWithSnackbar]
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!deactivatingUser) return;
    await runWithSnackbar(
      () => updateAuthorizedUserStatus({
        id: deactivatingUser.id,
        action: AuthorizedUserStatusAction.DEACTIVATE,
      }),
      `${getFullName(deactivatingUser)} has been deactivated`,
      SnackbarState.negative
    );
  }, [deactivatingUser, updateAuthorizedUserStatus, runWithSnackbar]);

  const handleConfirmReactivate = useCallback(async () => {
    if (!reactivatingUser) return;
    await runWithSnackbar(
      () => updateAuthorizedUserStatus({
        id: reactivatingUser.id,
        action: AuthorizedUserStatusAction.ACTIVATE,
      }),
      `${getFullName(reactivatingUser)} has been activated`
    );
  }, [reactivatingUser, updateAuthorizedUserStatus, runWithSnackbar]);

  return (
    <AuthorizedUsersComponent
      authorizedUsers={sortedUsers}
      filteredUsers={filteredUsers}
      filterStatuses={filterStatuses}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      editingUser={editingUser}
      deactivatingUser={deactivatingUser}
      reactivatingUser={reactivatingUser}
      isSubmitting={isCreating || isUpdating}
      isDeactivating={isStatusUpdating}
      isReactivating={isStatusUpdating}
      onAddNew={handleOpenAddModal}
      onEdit={handleEdit}
      onDeactivate={setDeactivatingUser}
      onReactivate={setReactivatingUser}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onConfirmDeactivate={handleConfirmDeactivate}
      onConfirmReactivate={handleConfirmReactivate}
      onCloseModal={handleCloseModal}
      onCloseDeactivateModal={handleCloseDeactivateModal}
      onCloseReactivateModal={handleCloseReactivateModal}
      onFilterChange={handleFilterChange}
    />
  );
};

export default AuthorizedUsersContainer;
